import { useSuspenseQuery } from '@tanstack/react-query';

import type * as api from '@gitbook/api-client';

import { apiQuery } from '../../state';
import {
    getSiteFromOrgAllContent,
    getSiteSpaceInSiteStructure,
    useSpacePrimarySite,
} from '../Organization';
import { getSiteURLForContent, type SitePreviewContent } from './getSiteURLForContent';

/**
 * Compute the URL to publicly preview a specific content in a space.
 * If the space can't be previewed, it returns null.
 */
export function useSiteContentPreviewURL(
    content: Omit<SitePreviewContent, 'siteSpaceId'> & { spaceId: string; organizationId: string }
) {
    const { data: orgAllContent } = useSuspenseQuery(
        apiQuery('getOrganizationAllContent', {
            organizationId: content.organizationId,
        })
    );
    const siteLookUp = useSpacePrimarySite(orgAllContent, content.spaceId);
    if (!siteLookUp) {
        return null;
    }
    return getSitePreviewURL({
        orgAllContent,
        siteId: siteLookUp.site.id,
        content: {
            siteSpaceId: siteLookUp.siteSpace.id,
            changeRequestNumber: content.changeRequestNumber,
            revisionId: content.revisionId,
            pagePath: content.pagePath,
        },
    });
}

/**
 * Get the preview URL of a site.
 */
export function getSitePreviewURL(props: {
    orgAllContent: api.OrganizationAllContent;
    siteId: string | null;
    content: SitePreviewContent;
}) {
    const { orgAllContent, siteId, content } = props;

    const siteEntry = siteId ? getSiteFromOrgAllContent(orgAllContent, siteId) : null;
    if (!siteEntry) {
        return null;
    }
    const siteSpace = content.siteSpaceId
        ? getSiteSpaceInSiteStructure(siteEntry.item.structure, content.siteSpaceId)
        : null;
    const rootURL = siteSpace?.urls.published ?? siteEntry?.item.site.urls.published;
    if (!rootURL) {
        return null;
    }
    return getSiteURLForContent(rootURL, {
        siteSpaceId: content.siteSpaceId,
        changeRequestNumber: content.changeRequestNumber,
        revisionId: content.revisionId,
        pagePath: content.pagePath,
    }).toString();
}
